import type { Country } from 'react-phone-number-input';

/** `user.country.name` (see `useDynamicCountry`). */
export type DynamicCountry = string;

const COUNTRY_NAME_TO_ISO: Record<string, Country> = {
  australia: 'AU',
  'new zealand': 'NZ',
};

/** ISO 3166-1 alpha-2 for phone inputs from the user's country name. */
export const dynamicCountryToPhoneIso = (countryName: string): Country => {
  const key = countryName?.trim().toLowerCase();
  if (!key) return 'AU';
  return COUNTRY_NAME_TO_ISO[key] ?? 'AU';
};

export const isNewZealandCountry = (countryName: string): boolean =>
  countryName?.trim().toLowerCase() === 'new zealand';

export const isAustraliaCountry = (countryName: string): boolean =>
  countryName?.trim().toLowerCase() === 'australia';

export const getAbnOrNzbnLabel = (countryName: string): 'ABN' | 'NZBN' =>
  isAustraliaCountry(countryName) ? 'ABN' : 'NZBN';

export const getStateOrRegionLabel = (
  countryName: string,
): 'State' | 'Region' => (isAustraliaCountry(countryName) ? 'State' : 'Region');

export const getClientAbnFieldLabel = (
  countryName: string,
): 'Client ABN' | 'Client NZBN' =>
  isAustraliaCountry(countryName) ? 'Client ABN' : 'Client NZBN';

export const getAbnNumberFieldLabel = (countryName: string): 'ABN' | 'NZBN' =>
  isAustraliaCountry(countryName) ? 'ABN' : 'NZBN';

/** Google Places API expects lowercase ISO 3166-1 Alpha-2 codes, e.g. ['au'], ['nz']. */
export const getCountryRestrictionsForPlaces = (
  countryName: string,
): [string] => [dynamicCountryToPhoneIso(countryName).toLowerCase()];

/**
 * When the user's country is New Zealand, rewrite Australia's +61 international prefix to +64.
 */
export const replaceCallingCodeWithNz = (
  value: string | null | undefined,
  countryName: string,
): string | undefined | null => {
  if (value == null || value === '') return value;
  if (!isNewZealandCountry(countryName)) return value;
  const s = String(value).trim();
  if (s.startsWith('+61')) {
    return `+64${s.slice(3)}`;
  }
  return value;
};
